import { useStore } from '@nanostores/react'
import { Fit, Layout, useRive } from '@rive-app/react-canvas'
import { AnimatePresence, LazyMotion } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import rivKV from '~/assets/rive/kv.riv'
import { getMessage } from '~/config'
import { RewardFlow } from '~/features/reward-flow'
import { useBalanceQuery } from '~/features/reward-flow/hooks/use-balance-query'
import { rewardFlow } from '~/features/reward-flow/store'
import { useClaimRewardMutation } from '~/hooks/use-claim-reward-mutation'
import { useJumpMutation } from '~/hooks/use-jump-mutation'
import {
  markExtensionNoticeSeen,
  shouldShowExtensionNotice,
} from '~/lib/extension-notice'
import { MutationGatedError } from '~/lib/gated-mutation'
import { motion } from '~/lib/motion'
import { queryClient } from '~/lib/query-client'
import { $config } from '~/store/config'
import { dialog } from '@forest/shared/react'
import { loadingOverlay } from '~/store/loading-overlay'
import { $subscribedView } from '~/store/subscribed-view'
import { $userInfo } from '~/store/user-info'
import { SideLightWaves } from './components/catchup-glow'
import { CloseButton } from './components/close-button'
import { EventDetailsDialog } from './components/event-details-dialog'
import { FinalRewardPreviewDialog } from './components/final-reward-preview-dialog'
import { FixedFinalRewards } from './components/fixed-final-rewards'
import { GeneralTrackBadge } from './components/general-track-badge'
import { EventCountdownFrame } from './components/graphics/countdown-frame'
import { CountdownIcon } from './components/icons/countdown'
import { InfoIcon } from './components/icons/info'
import { JumpAnchor } from './components/jump-anchor'
import { JumpConfirmDialog } from './components/jump-confirm-dialog'
import { PaidTrackBadge } from './components/paid-track-badge'
import { PassPaywallDialog } from './components/pass-paywall-dialog'
import {
  ProgressColumn,
  STEP,
  type JumpPointPill,
} from './components/progress-column'
import { ReminderDialog } from './components/reminder-dialog'
import { RewardItem } from './components/reward-item'
import { SubscribedView } from './components/subscribed-view'
import { getBoostOrdinal } from './helpers/get-boost-ordinal'
import { getEventStatus } from './helpers/get-event-status'
import { getJumpDialogRoute } from './helpers/get-jump-dialog-route'
import { getVisibleJumpPoints } from './helpers/get-visible-jump-points'
import { isAfterEarningWindow } from './helpers/is-after-earning-window'
import { isRewardLocked } from './helpers/is-reward-locked'
import {
  activityQueryKey,
  getActivityId,
  getCachedActivityScore,
  useActivityQuery,
  type Reward,
} from './hooks/use-activity-query'
import { useCatchupAnimation } from './hooks/use-catchup-animation'
import { useCountdown } from './hooks/use-countdown'
import { useEntitlementQuery } from './hooks/use-entitlement-query'
import { useJumpAnchor } from './hooks/use-jump-anchor'
import { useTimestamp } from './hooks/use-timestamp'
import { m } from './paraglide/messages'
import { native } from '@forest/native-services'

const DAY_MS = 24 * 60 * 60 * 1000

export function App() {
  const config = useStore($config)
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)

  useEffect(() => {
    native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: 'game_pass_main_page' },
    })
  }, [])

  // preloads for the reward flow; also the jump dialog routing source
  const balanceQuery = useBalanceQuery()

  const { data } = useActivityQuery()
  const { data: entitlement } = useEntitlementQuery()

  useEffect(() => {
    if (data && entitlement) {
      loadingOverlay.hide()
    }
  }, [data, entitlement])

  const timestamp = useTimestamp()

  const eventStatus = data?.visibleRange
    ? getEventStatus(data.visibleRange, timestamp.getTime())
    : null

  const isClaimOnly =
    eventStatus === 'active' && data?.validRange
      ? isAfterEarningWindow(data.validRange, timestamp.getTime())
      : false

  // Extension notice: seen-on-show, so the flag is written before the dialog
  // opens — leaving the page without tapping OK still counts. The ref keeps
  // the 60s timestamp refetch from re-opening it if the storage write failed.
  const hasShownExtensionNoticeRef = useRef(false)
  useEffect(() => {
    if (hasShownExtensionNoticeRef.current) return
    if (!entitlement || eventStatus !== 'active') return
    if (!shouldShowExtensionNotice(timestamp.getTime())) return
    hasShownExtensionNoticeRef.current = true
    markExtensionNoticeSeen()
    dialog.open({
      title: m.dialog_summer_focus_title_extend(),
      description: m.dialog_summer_focus_content_extend(),
      button: m.ok(),
    })
  }, [entitlement, eventStatus, timestamp])

  const userInfo = useStore($userInfo)
  const visibleJumpPoints =
    data && userInfo
      ? getVisibleJumpPoints(config?.jump_points, {
          validRange: data.validRange,
          accumulatedScore: data.currentScore,
          server: userInfo.server,
          now: timestamp.getTime(),
        })
      : []
  const purchasableJumpPoints = visibleJumpPoints.filter(
    (jumpPoint) => jumpPoint.status === 'purchasable',
  )

  const pendingCountdown = useCountdown(
    eventStatus === 'pending' ? data?.visibleRange?.lower : undefined,
    timestamp,
  )
  const activeCountdown = useCountdown(
    eventStatus === 'active' ? data?.validRange?.upper : undefined,
    timestamp,
  )

  const [passUnlockAnimation, setPassUnlockAnimation] = useState<{
    token: number
    stepIds: number[]
  }>({ token: 0, stepIds: [] })
  const claimMutation = useClaimRewardMutation()
  const [claimingTrack, setClaimingTrack] = useState<'general' | 'paid' | null>(
    null,
  )
  const [isBottomInView, setIsBottomInView] = useState(false)
  const bottomRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const observer = new IntersectionObserver(([entry]) => {
      setIsBottomInView(entry.isIntersecting)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // The fixed final-rewards footer renders while the page bottom is out of
  // view; the anchor's pill visibility band tracks that.
  const jumpAnchor = useJumpAnchor(
    purchasableJumpPoints.map((jumpPoint) => jumpPoint.required_score),
    !isBottomInView,
  )

  const [finalRewardPreviewDialogState, setFinalRewardPreviewDialogState] =
    useState<{
      isOpen: boolean
      reward: 'free' | 'paid'
    }>({ isOpen: false, reward: 'free' })

  const [jumpConfirmDialogState, setJumpConfirmDialogState] = useState<{
    isOpen: boolean
    requiredScore: number
    gemPrice: number
    productId: number
  }>({ isOpen: false, requiredScore: 0, gemPrice: 0, productId: 0 })

  const handleJumpPointClick = (jumpPoint: JumpPointPill) => {
    // Intent event: fires on every purchasable-pill tap, before the
    // sufficient/insufficient-Crystals routing below.
    void native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'btn',
        app_action: 'click',
        component_name: `game_pass_boost_${getBoostOrdinal(
          config?.jump_points,
          jumpPoint.requiredScore,
        )}`,
      },
    })
    const route = getJumpDialogRoute(balanceQuery.data?.gem, jumpPoint.gemPrice)
    if (route === 'confirm') {
      // Purchasable points always carry a product id for the active server
      // (getVisibleJumpPoints filters on it).
      const productId = purchasableJumpPoints.find(
        (point) => point.required_score === jumpPoint.requiredScore,
      )?.product_id[userInfo!.server]
      if (productId == null) return
      setJumpConfirmDialogState({
        isOpen: true,
        requiredScore: jumpPoint.requiredScore,
        gemPrice: jumpPoint.gemPrice,
        productId,
      })
    } else if (route === 'insufficient') {
      dialog.open({
        title: m.dialog_purchase_insufficient_gems_title(),
        description: m.dialog_purchase_insufficient_gems_context(),
        button: m.ok(),
      })
    }
  }

  const jumpMutation = useJumpMutation()
  const catchup = useCatchupAnimation()
  const catchupFiredStepIdsRef = useRef<Set<number>>(new Set())

  const handleJump = async () => {
    const { productId, requiredScore } = jumpConfirmDialogState
    // Snapshot the pre-jump state now — after the await, `data` already
    // holds the refetched end state.
    const fromScore = data?.currentScore ?? null
    const checkpoints = data?.checkpoints ?? null
    // Arm the old-score display override before the mutation's awaited
    // onSuccess invalidation can commit a render: the refetched end state
    // must land behind the override, never flash before the catch-up run.
    // Identity render until then — the data still holds the old score.
    if (fromScore !== null) catchup.arm(fromScore)
    try {
      await jumpMutation.mutateAsync({ productId, requiredScore })
      setJumpConfirmDialogState((state) => ({ ...state, isOpen: false }))

      // The mutation's awaited onSuccess invalidation has refetched the
      // activity, so the cache holds the landed score. The catch-up run is
      // pure presentation of that end state: it only tweens old → new.
      const toScore = getCachedActivityScore(queryClient) ?? requiredScore
      if (fromScore !== null && checkpoints !== null) {
        // Fresh Rive unlock-token run: rewards the head passes are appended
        // under this one token so each plays its lock-unlock exactly once.
        catchupFiredStepIdsRef.current = new Set()
        setPassUnlockAnimation((state) => ({
          token: state.token + 1,
          stepIds: [],
        }))
        catchup.start({ fromScore, toScore, checkpoints, step: STEP })
      }
    } catch (error) {
      // Outcome dialogs are opened by the mutation's onError. A gated
      // (double-fired) call must not close the dialog under the live one —
      // nor drop the armed override that live call still owns.
      // No catch-up on any failure — including the polling timeout, where
      // the queries intentionally stay stale: disarm back to the idle render.
      if (!(error instanceof MutationGatedError)) {
        setJumpConfirmDialogState((state) => ({ ...state, isOpen: false }))
        catchup.disarm()
      }
    }
  }

  // As the catch-up head passes a paid-track reward, fire its Rive
  // lock-unlock (the same token machinery the subscription success path
  // uses) — in passing order, once each. Locked items ignore the token and
  // keep their lock, matching the non-subscriber design frames.
  const catchupDisplayScore = catchup.displayScore
  const catchupFromScore = catchup.fromScore
  useEffect(() => {
    if (!data || catchupDisplayScore === null || catchupFromScore === null) {
      return
    }
    const fired = catchupFiredStepIdsRef.current
    const passedStepIds = data.paidTrackRewards
      .filter(
        (reward) =>
          reward.requiredScore > catchupFromScore &&
          reward.requiredScore <= catchupDisplayScore &&
          !fired.has(reward.stepId) &&
          !isRewardLocked(reward, entitlement),
      )
      .map((reward) => reward.stepId)
    if (passedStepIds.length === 0) return
    for (const stepId of passedStepIds) fired.add(stepId)
    setPassUnlockAnimation((state) => ({
      token: state.token,
      stepIds: [...state.stepIds, ...passedStepIds],
    }))
  }, [catchupDisplayScore, catchupFromScore, data, entitlement])

  function isLocked(reward: Reward) {
    return isRewardLocked(reward, entitlement)
  }

  const handleClaimReward = async (
    stepId: number,
    track: 'general' | 'paid',
  ) => {
    if (claimMutation.isPending) return
    loadingOverlay.show()
    setClaimingTrack(track)
    const activityId = getActivityId()
    try {
      const { items } = await claimMutation.mutateAsync({
        activityId,
        stepId,
      })
      loadingOverlay.hide()

      native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name:
            track === 'paid'
              ? 'game_pass_paid_reward_claimed'
              : 'game_pass_free_reward_claimed',
        },
      })

      await rewardFlow.start({
        previewAnim: null,
        items,
      })

      loadingOverlay.show()
      await queryClient.invalidateQueries({
        queryKey: activityQueryKey(activityId),
      })
    } catch (err) {
      console.error('Error claiming reward: ', err)
    } finally {
      loadingOverlay.hide()
    }
  }

  const handleSubscriptionSuccess = () => {
    if (!data || !entitlement) {
      setPassUnlockAnimation((state) => ({
        token: state.token + 1,
        stepIds: [],
      }))
      return
    }

    const stepIds = data.paidTrackRewards
      .filter((reward) => isLocked(reward))
      .map((reward) => reward.stepId)

    setPassUnlockAnimation((state) => ({
      token: state.token + 1,
      stepIds,
    }))
  }

  // During a catch-up run the refetched data is already the end state — the
  // page renders reward statuses as of the rolling score instead, so each
  // reward flips claimable exactly as the head passes it. Identity when idle,
  // keeping the settled render pixel-identical to a plain refetch.
  const toDisplayedReward = (reward: Reward): Reward =>
    catchupDisplayScore !== null &&
    reward.status === 'claimable' &&
    reward.requiredScore > catchupDisplayScore
      ? { ...reward, status: 'pending' }
      : reward
  const displayedData = data && {
    ...data,
    paidTrackRewards: data.paidTrackRewards.map(toDisplayedReward),
    generalTrackRewards: data.generalTrackRewards.map(toDisplayedReward),
  }

  const previewedFinalReward =
    finalRewardPreviewDialogState.reward === 'paid'
      ? displayedData?.paidTrackRewards.at(-1)
      : displayedData?.generalTrackRewards.at(-1)

  // The fixed footer previews the tracks' final rewards.
  const finalCheckpoint = displayedData?.checkpoints.at(-1)
  const finalPaidReward = displayedData?.paidTrackRewards.at(-1)
  const finalGeneralReward = displayedData?.generalTrackRewards.at(-1)

  return (
    <>
      {config && (
        <LazyMotion features={loadMotionFeatures}>
          <FinalRewardPreviewDialog
            isOpen={finalRewardPreviewDialogState.isOpen}
            reward={finalRewardPreviewDialogState.reward}
            titleKey={previewedFinalReward?.titleKey ?? null}
            contentType={previewedFinalReward?.type ?? null}
            isClaimed={previewedFinalReward?.status === 'claimed'}
            isClaimable={
              !!previewedFinalReward &&
              previewedFinalReward.status === 'claimable' &&
              !isLocked(previewedFinalReward)
            }
            isUnlocked={
              !!previewedFinalReward && !isLocked(previewedFinalReward)
            }
            onClaim={() => {
              if (!previewedFinalReward) return
              setFinalRewardPreviewDialogState((s) => ({
                ...s,
                isOpen: false,
              }))
              handleClaimReward(
                previewedFinalReward.stepId,
                finalRewardPreviewDialogState.reward === 'paid'
                  ? 'paid'
                  : 'general',
              )
            }}
            onPurchase={() => {
              setFinalRewardPreviewDialogState((s) => ({
                ...s,
                isOpen: false,
              }))
              setIsPaywallOpen(true)
            }}
            onClose={() => {
              setFinalRewardPreviewDialogState((state) => ({
                ...state,
                isOpen: false,
              }))
            }}
          />
          <PassPaywallDialog
            isOpen={isPaywallOpen}
            onClose={() => setIsPaywallOpen(false)}
          />
          <SubscribedView
            onComplete={() => {
              handleSubscriptionSuccess()
              $subscribedView.set({ isOpen: false, isTrial: false })
              queryClient.invalidateQueries({ queryKey: ['entitlement'] })
            }}
          />
          <ReminderDialog />
          <JumpConfirmDialog
            isOpen={jumpConfirmDialogState.isOpen}
            requiredScore={jumpConfirmDialogState.requiredScore}
            gemPrice={jumpConfirmDialogState.gemPrice}
            isSubscriber={entitlement?.isSubscriber ?? false}
            isJumping={jumpMutation.isPending}
            onJump={handleJump}
            onClose={() =>
              setJumpConfirmDialogState((state) => ({
                ...state,
                isOpen: false,
              }))
            }
          />

          <div
            className="relative min-h-screen"
            style={{ background: config.background }}
          >
            <TopBush />
            <BottomBush />

            <CloseButton
              className="fixed left-4 top-[calc(16px+var(--safe-area-top))] z-10"
              onClick={() => {
                // Claims/jumps on this page credit coins/gems server-side
                // only — sync before closing so the app's local balance
                // reflects them.
                void native.data.fullSync()
                native.general.close()
              }}
            />

            {eventStatus === 'pending' || eventStatus === 'ended' ? (
              <>
                <div className="relative mx-auto flex min-h-screen max-w-[640px] flex-col items-center justify-center text-white">
                  <div className="px-6 pb-6 pt-4">
                    <h1 className="w-full text-[36px] font-bold">
                      {eventStatus === 'pending'
                        ? m.dialog_event_not_yet_started_title()
                        : m.dialog_event_is_over_title()}
                    </h1>
                    <p className="mt-2 w-full font-semibold opacity-70">
                      {eventStatus === 'pending'
                        ? m.dialog_event_not_yet_started_description()
                        : m.dialog_event_is_over_description()}
                    </p>
                  </div>
                  {/* countdown */}
                  {eventStatus === 'pending' && (
                    <div className="flex items-center justify-center gap-[10px] p-6">
                      <div className="relative size-[97px]">
                        <EventCountdownFrame />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-[36px] font-bold">
                            {String(pendingCountdown.days).padStart(2, '0')}
                          </span>
                          <span className="mt-[2px] font-semibold">
                            {m.day()}
                          </span>
                        </div>
                      </div>

                      <span className="text-[25px]">:</span>

                      <div className="relative size-[97px]">
                        <EventCountdownFrame />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-[36px] font-bold">
                            {String(pendingCountdown.hours).padStart(2, '0')}
                          </span>
                          <span className="mt-[2px] font-semibold">
                            {m.hour()}
                          </span>
                        </div>
                      </div>

                      <span className="text-[25px]">:</span>

                      <div className="relative size-[97px]">
                        <EventCountdownFrame />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-[36px] font-bold">
                            {String(pendingCountdown.minutes).padStart(2, '0')}
                          </span>
                          <span className="mt-[2px] font-semibold">
                            {m.min()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Top row */}
                <div className="relative flex justify-end gap-1 px-4 pb-1 pt-[calc(18px+var(--safe-area-top))]">
                  <EventDetailsDialog />
                  <span className="flex h-9 items-center gap-1 rounded-[20px] bg-black/15 px-3 text-white">
                    <CountdownIcon />
                    <span>
                      {activeCountdown.days > 0 &&
                        `${activeCountdown.days} ${m.day()} `}
                      {activeCountdown.hours > 0 &&
                        `${activeCountdown.hours} ${m.hour()}`}
                      {activeCountdown.days === 0 &&
                        activeCountdown.hours === 0 &&
                        `${activeCountdown.minutes} ${m.min()}`}
                    </span>
                  </span>
                </div>

                {/* Header */}
                <div className="relative mx-auto flex max-w-[640px] flex-col gap-4 px-6 pb-6 pt-4 text-center text-white">
                  <h1 className="text-[26px] font-bold">
                    {getMessage(config.title_key)}
                  </h1>
                  <p className="font-semibold">
                    {getMessage(config.subtitle_key)}
                  </p>
                  {isClaimOnly && (
                    <div
                      className="relative mx-auto flex h-9 items-center gap-1 rounded-full bg-white px-3 text-xs text-[#DB5417]"
                      style={{
                        background:
                          'linear-gradient(180deg, #FFF1D5 20%, #FCDAA0 85%)',
                        boxShadow: '0 2px 8px 0 rgba(203, 139, 29, 0.50)',
                      }}
                    >
                      <svg
                        width="17"
                        height="15"
                        viewBox="0 0 17 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="absolute left-1/2 top-[33px] -translate-x-1/2"
                      >
                        <path
                          d="M6.69853 13.2864C7.48187 14.5397 9.30719 14.5397 10.0905 13.2864L16.482 3.06C17.3146 1.7279 16.3569 0 14.786 0L2.00303 0C0.432157 0 -0.52553 1.7279 0.307029 3.06L6.69853 13.2864Z"
                          fill="#FCDAA0"
                        />
                      </svg>
                      <InfoIcon />
                      {m.claim_only()}
                    </div>
                  )}
                </div>

                {displayedData && (
                  <>
                    {/* Main content */}
                    <div className="flex justify-center pb-[100px]">
                      {/* Plus track */}
                      <div className="flex w-[115px] shrink-0 flex-col items-center gap-5">
                        <PaidTrackBadge
                          onClick={() => setIsPaywallOpen(true)}
                          isUnlocked={entitlement?.isSubscriber ?? false}
                        />

                        {displayedData.paidTrackRewards.map((reward, i) => {
                          const isLast =
                            i === displayedData.paidTrackRewards.length - 1
                          const unlockAnimationToken =
                            passUnlockAnimation.stepIds.includes(reward.stepId)
                              ? passUnlockAnimation.token
                              : undefined

                          return (
                            <RewardItem
                              key={reward.stepId}
                              in="paid-track"
                              status={reward.status}
                              iconUrl={reward.iconUrl}
                              reward={{
                                type: reward.type,
                                amount: reward.amount,
                              }}
                              isLast={isLast}
                              locked={isLocked(reward)}
                              badgeTitleKey={isLast ? reward.titleKey : null}
                              unlockAnimationToken={unlockAnimationToken}
                              onClick={() => {
                                if (isLast) {
                                  setFinalRewardPreviewDialogState({
                                    isOpen: true,
                                    reward: 'paid',
                                  })
                                  return
                                }
                                handleClaimReward(reward.stepId, 'paid')
                              }}
                              onLockedClick={() => setIsPaywallOpen(true)}
                            />
                          )
                        })}
                      </div>

                      {/* Progress column */}
                      <ProgressColumn
                        current={
                          catchupDisplayScore ?? displayedData.currentScore
                        }
                        checkpoints={displayedData.checkpoints}
                        jumpPoints={visibleJumpPoints.map((jumpPoint) => ({
                          requiredScore: jumpPoint.required_score,
                          gemPrice: jumpPoint.gem_price,
                          status: jumpPoint.status,
                          daysUntilAvailable: Math.max(
                            0,
                            Math.ceil(
                              (jumpPoint.opensAt - timestamp.getTime()) /
                                DAY_MS,
                            ),
                          ),
                        }))}
                        onJumpPointClick={handleJumpPointClick}
                        onJumpPointPillRef={jumpAnchor.observePill}
                        catchup={catchup}
                      />

                      {/* General track */}
                      <div className="flex w-[115px] shrink-0 flex-col items-center gap-5">
                        <GeneralTrackBadge />
                        {displayedData.generalTrackRewards.map((reward, i) => {
                          const isLast =
                            i === displayedData.generalTrackRewards.length - 1
                          const item = (
                            <RewardItem
                              key={reward.stepId}
                              in="general-track"
                              status={reward.status}
                              iconUrl={reward.iconUrl}
                              reward={{
                                type: reward.type,
                                amount: reward.amount,
                              }}
                              isLast={isLast}
                              badgeTitleKey={isLast ? reward.titleKey : null}
                              onClick={() => {
                                if (isLast) {
                                  setFinalRewardPreviewDialogState({
                                    isOpen: true,
                                    reward: 'free',
                                  })
                                  return
                                }
                                handleClaimReward(reward.stepId, 'general')
                              }}
                            />
                          )
                          return isLast ? (
                            <div key={reward.stepId} ref={bottomRef}>
                              {item}
                            </div>
                          ) : (
                            item
                          )
                        })}
                      </div>
                    </div>

                    <AnimatePresence initial={false}>
                      {!isBottomInView &&
                        finalCheckpoint !== undefined &&
                        finalPaidReward &&
                        finalGeneralReward && (
                          <motion.div
                            className="fixed bottom-0 left-0 right-0 mx-auto max-w-[393px]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{
                              backdropFilter: 'blur(6px)',
                            }}
                          >
                            <FixedFinalRewards
                              finalCheckpoint={finalCheckpoint}
                              paidIconUrl={finalPaidReward.iconUrl}
                              freeIconUrl={finalGeneralReward.iconUrl}
                              paidTitleKey={finalPaidReward.titleKey}
                              freeTitleKey={finalGeneralReward.titleKey}
                              paidStatus={finalPaidReward.status}
                              paidLocked={isLocked(finalPaidReward)}
                              freeStatus={finalGeneralReward.status}
                              openPreview={(type) => {
                                setFinalRewardPreviewDialogState({
                                  isOpen: true,
                                  reward: type,
                                })
                              }}
                            />
                          </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence initial={false}>
                      {jumpAnchor.isVisible && !catchup.isActive && (
                        <JumpAnchor onClick={jumpAnchor.scrollToJumpPoints} />
                      )}
                    </AnimatePresence>

                    {/* Post-jump catch-up run: all interaction is locked
                        until the animation settles, and the side light
                        waves wash the viewport edges. */}
                    {catchup.isActive && (
                      <div
                        className="fixed inset-0 z-[60]"
                        style={{ touchAction: 'none' }}
                        aria-hidden
                      />
                    )}
                    <AnimatePresence>
                      {catchup.isActive && (
                        <SideLightWaves
                          key="side-waves"
                          phase={catchup.phase}
                        />
                      )}
                    </AnimatePresence>
                  </>
                )}
              </>
            )}
          </div>

          <RewardFlow
            // TODO: move this to reward flow store
            background={
              claimingTrack === 'general'
                ? config.free_reward_flow_background
                : config.paid_reward_flow_background
            }
          />
        </LazyMotion>
      )}
    </>
  )
}

function TopBush() {
  const riveSrc = $config.get()!.main_visual_rive_url || rivKV
  const { RiveComponent } = useRive({
    src: riveSrc,
    artboard: 'kv_top',
    stateMachines: ['State Machine 1'],
    autoplay: true,
    layout: new Layout({
      fit: Fit.FitHeight,
    }),
  })

  return (
    <RiveComponent className="pointer-events-none absolute left-0 right-0 top-0 h-[350px]" />
  )
}

function BottomBush() {
  const riveSrc = $config.get()!.main_visual_rive_url || rivKV
  const { RiveComponent } = useRive({
    src: riveSrc,
    artboard: 'kv_bot',
    stateMachines: ['State Machine 1'],
    autoplay: true,
    layout: new Layout({
      fit: Fit.FitHeight,
    }),
  })

  return (
    <RiveComponent className="pointer-events-none fixed bottom-0 left-0 right-0 h-[150px]" />
  )
}

const loadMotionFeatures = async () =>
  import('~/lib/motion-features').then((mod) => mod.default)
